import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { Html5Qrcode } from "html5-qrcode";
import uiConfig from "../uiConfig";
import { getMessageForFetchError } from "../shared/utils/apiError";
import ConfirmationModal from "../components/ConfirmationModal";
import EntityListBulkToolbar from "../components/entity/EntityListBulkToolbar";
import PhotographyConsentBadge from "../components/PhotographyConsentBadge";
import EventRegistrationQRCode from "../components/EventRegistrationQRCode";
import EventDetailModal from "../components/EventDetailModal";
import EventRegistrationKpiCards from "../components/EventRegistrationKpiCards";
import EventDescriptionPreview from "../components/EventDescriptionPreview";
import EventCardPriceMeta from "../components/EventCardPriceMeta";
import { PAYMENT_MODE_INTEGRATED, PAYMENT_MODE_PAYMENT_LINK } from "../shared/utils/eventPaymentMode";
import { IconCalendar, IconLocation, IconApprove, IconCancel, IconBacklog, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";
import { resolveEventBackgroundImageUrl } from "../shared/utils/eventBackgroundImage";
import { getUpcomingEventsSorted, pickDefaultUpcomingEvent } from "../shared/utils/eventUpcoming";
import { computeRegistrationMetrics } from "../shared/utils/eventRegistrationMetrics";
import { getFilterSelectionValidityAction } from "../shared/utils/filterSelectionValidity";
import { useLatestRequestSeq } from "../shared/hooks/useLatestRequestSeq";
import { formatPhoneDisplay, normalizePhoneDigits } from "../shared/utils/phoneFormat";
import { isPlaceholderEmail } from "../shared/utils/displayEmail";
import { sortRowsByConfig } from "../shared/utils/sortRowsByConfig";
import {
  getParticipantRegisteredAtCell,
  getParticipantRegTypeCell,
  buildParticipantSortRow,
} from "../shared/utils/eventParticipantTableHelpers";
import {
  awaitScannerLifecycleSafe,
  chainStopAfterLifecycle,
  getHtml5QrcodeCameraErrorMessage,
  installHtml5QrcodeBenignRejectionGuard,
  isBenignHtml5CameraTeardownError,
  listPreferredQrCameraIds,
  stopHtml5QrcodeScannerAndClearRef,
  tryStopHtml5QrcodeInstance,
} from "../shared/utils/html5QrcodeCameraTeardown";
import {
  SCAN_MODAL_TONES,
  scanModalBodyOuterStyle,
  SCAN_MODAL_DETAIL_LABEL_STYLE,
  SCAN_MODAL_DETAIL_NAME_STYLE,
  SCAN_MODAL_DETAIL_META_STYLE,
  scanModalOutcomeBoxStyle,
} from "../shared/utils/eventCheckInModalMessageLayout";
import {
  getPhotographyConsentLabel,
  PHOTOGRAPHY_CONSENT_CHECKIN_ALERT,
  shouldWarnPhotographyConsentAtCheckIn,
} from "../shared/utils/photographyConsent";
import {
  continueCheckInFromPreflight,
  completeWaitlistAllowCheckIn,
  evaluateParticipantCheckInPreflight,
  formatCheckInParticipantNameBracket,
} from "../shared/utils/eventCheckInFlow";
import EventSectionTabs, { EVENT_TAB_CHECKIN } from "../components/EventSectionTabs";
import SectionTabsPageHeader from "../components/SectionTabsPageHeader";
import CloseEventRecord from "./CloseEventRecord";
import { useAuth } from "../contexts/AuthContext";
import "./EventQRCheckIn.css";


import { apiFetch } from "../shared/api/apiFetch";

/** Waitlisted headcount from registration metrics (capacity bump UI). */
function getWaitlistedCountForCapacityBump(registrationMetrics) {
  return Number.isFinite(Number(registrationMetrics?.waitlisted_count))
    ? Math.max(0, Math.floor(Number(registrationMetrics.waitlisted_count)))
    : 0;
}

function clampCapacityBumpDelta(n) {
  const v = Number.isFinite(Number(n)) ? Math.floor(Number(n)) : 1;
  return Math.max(1, v);
}

/**
 * Build POST /updateEvent body from an event document (e.g. getParticipantWithEvent / getOpenEvents).
 * Returns null if required fields are missing.
 */
function buildUpdateEventBodyFromEventLean(accountId, eventDoc, maxCapacity) {
  if (!accountId || !eventDoc) return null;
  const eid = eventDoc._id != null ? String(eventDoc._id) : "";
  if (!eid) return null;
  const nameTrim = (eventDoc.name != null ? String(eventDoc.name) : "").trim();
  const program_id = (eventDoc.program_id != null ? String(eventDoc.program_id) : "").trim();
  const event_organizer_id = (eventDoc.event_organizer_id != null ? String(eventDoc.event_organizer_id) : "").trim();
  if (!nameTrim || !program_id || !event_organizer_id) return null;
  const audienceTypeRaw = (eventDoc.audience_type != null ? String(eventDoc.audience_type) : "").trim();
  const audience_type = ["Youth Only", "Adult Only", "All Ages"].includes(audienceTypeRaw) ? audienceTypeRaw : "Youth Only";
  const eventType = (eventDoc.event_type || "Free") === "Paid" ? "Paid" : "Free";
  const maxNum = Math.max(0, Math.floor(Number(maxCapacity)));
  const body = {
    account_id: accountId,
    _id: eid,
    name: nameTrim,
    description: (eventDoc.description != null ? String(eventDoc.description) : "").trim(),
    repeat_rule: eventDoc.repeat_rule || null,
    location: (eventDoc.location != null ? String(eventDoc.location) : "").trim() || undefined,
    program_id,
    event_organizer_id,
    background_image_url: (eventDoc.background_image_url != null ? String(eventDoc.background_image_url) : "").trim() || undefined,
    visibility: eventDoc.visibility === "private" ? "private" : "public",
    max_capacity: maxNum,
    audience_type,
    event_type: eventType,
  };
  if (eventDoc.start_datetime) body.start_datetime = eventDoc.start_datetime;
  if (eventDoc.end_datetime) body.end_datetime = eventDoc.end_datetime;
  if (eventType === "Paid") {
    const pc = eventDoc.price_cents != null ? Number(eventDoc.price_cents) : 0;
    if (!Number.isNaN(pc) && pc > 0) body.price_cents = Math.round(pc);
    const paymentMode = eventDoc.payment_mode != null ? String(eventDoc.payment_mode).trim() : "";
    if (paymentMode === PAYMENT_MODE_PAYMENT_LINK || paymentMode === PAYMENT_MODE_INTEGRATED) {
      body.payment_mode = paymentMode;
    }
    const paymentLinkUrl = eventDoc.payment_link_url != null ? String(eventDoc.payment_link_url).trim() : "";
    if (paymentMode === PAYMENT_MODE_PAYMENT_LINK && paymentLinkUrl) {
      body.payment_link_url = paymentLinkUrl;
    }
  }
  if (eventDoc.status === "Closed") {
    if (eventDoc.impact_hours_entered != null && eventDoc.impact_hours_entered !== "" && !Number.isNaN(Number(eventDoc.impact_hours_entered))) {
      body.impact_hours_entered = Number(eventDoc.impact_hours_entered);
    }
  }
  return body;
}

const QR_READER_ELEMENT_ID = "event-qr-checkin-reader";

async function postMarkParticipantNoShow(serverUrl, accountId, participantId) {
  return apiFetch(`${serverUrl}/markParticipantNoShow`, {
    method: "POST",
    body: JSON.stringify({ account_id: accountId, participant_id: participantId }),
  });
}

async function postMarkParticipantRegistered(serverUrl, accountId, participantId) {
  return apiFetch(`${serverUrl}/markParticipantRegistered`, {
    method: "POST",
    body: JSON.stringify({ account_id: accountId, participant_id: participantId }),
  });
}

/** User-facing outcome text for scan modal + ErrorBanner (same strings everywhere). */
function getCheckInScanResultMessage(result) {
  if (!result) return null;
  if (result.error) return result.error;
  const nameBracket = formatCheckInParticipantNameBracket(result.participantName);
  if (result.status === "attended") {
    return `This participant${nameBracket} is now checked in.`;
  }
  if (result.status === "already_scanned") {
    return `This participant${nameBracket} was already checked in. No change was made.`;
  }
  if (result.status === "waitlist_declined") {
    return `Check-in was not completed. This participant${nameBracket} remains on the waitlist.`;
  }
  if (result.status === "capacity_maxed") {
    return "Capacity is maxed out (Fill Rate 100%). Please manually adjust the capacity to allow this check-in.";
  }
  return null;
}

function PhotographyConsentCheckInAlert() {
  return <div className="photography-consent-checkin-alert">{PHOTOGRAPHY_CONSENT_CHECKIN_ALERT}</div>;
}

function showResultModal(setResult, { title, message, participantName, eventName, eventDateTime, registration_metrics, photography_consent } = {}) {
  setResult({
    status: "error",
    error: message || "Something went wrong.",
    title: title || "Check-in not completed",
    participantName,
    eventName,
    eventDateTime,
    registration_metrics,
    photography_consent,
  });
}

function buildScanResultModalContent(result, capacityBumpUi = null) {
  let toneKey = "success";
  if (result.error || result.status === "error") {
    toneKey = "error";
  } else if (result.status === "waitlist_declined") {
    toneKey = "waitlistDeclined";
  } else if (result.status === "already_scanned") {
    toneKey = "alreadyCheckedIn";
  } else if (result.status === "capacity_maxed") {
    toneKey = "error";
  }
  const tone = SCAN_MODAL_TONES[toneKey] || SCAN_MODAL_TONES.error;

  const scanOutcomeMessage = getCheckInScanResultMessage(result);
  const scanOutcomeIsError =
    Boolean(result.error) || result.status === "capacity_maxed" || result.status === "error";

  return (
    <div style={scanModalBodyOuterStyle(tone)}>
      {result.participantName != null && (
        <>
          <div style={SCAN_MODAL_DETAIL_LABEL_STYLE}>Participant</div>
          <p style={SCAN_MODAL_DETAIL_NAME_STYLE}>{result.participantName}</p>
          <div style={SCAN_MODAL_DETAIL_LABEL_STYLE}>Event</div>
          <p style={{ ...SCAN_MODAL_DETAIL_NAME_STYLE, fontWeight: 500 }}>{result.eventName}</p>
          {result.eventDateTime ? <p style={SCAN_MODAL_DETAIL_META_STYLE}>{result.eventDateTime}</p> : null}
          {shouldWarnPhotographyConsentAtCheckIn(result.photography_consent) ? <PhotographyConsentCheckInAlert /> : null}
          {scanOutcomeMessage ? (
            <div style={scanModalOutcomeBoxStyle(tone, scanOutcomeIsError ? 500 : 600)}>{scanOutcomeMessage}</div>
          ) : null}
        </>
      )}
      {result.participantName == null && result.error ? (
        <div style={{ ...scanModalOutcomeBoxStyle(tone, 500), marginTop: 0 }}>{result.error}</div>
      ) : null}
      {capacityBumpUi}
    </div>
  );
}

function scanResultModalTitle(result) {
  if (result && result.title) return result.title;
  if (result.error || result.status === "error") {
    return "Check-in not completed";
  }
  if (result.status === "waitlist_declined") {
    return "Check-in not completed";
  }
  if (result.status === "already_scanned") {
    return "Already checked in";
  }
  if (result.status === "attended") {
    return "Check-in confirmed";
  }
  if (result.status === "capacity_maxed") {
    return "Check-in not completed";
  }
  return "Scan result";
}

function WaitlistConfirmMessage({ participantName, eventName, eventDateTime, photography_consent }) {
  const tone = SCAN_MODAL_TONES.waitlistPrompt;
  return (
    <div style={scanModalBodyOuterStyle(tone)}>
      <div style={SCAN_MODAL_DETAIL_LABEL_STYLE}>Participant</div>
      <p style={SCAN_MODAL_DETAIL_NAME_STYLE}>{participantName}</p>
      <div style={SCAN_MODAL_DETAIL_LABEL_STYLE}>Event</div>
      <p style={{ ...SCAN_MODAL_DETAIL_NAME_STYLE, fontWeight: 500 }}>{eventName}</p>
      {eventDateTime ? <p style={SCAN_MODAL_DETAIL_META_STYLE}>{eventDateTime}</p> : null}
      {shouldWarnPhotographyConsentAtCheckIn(photography_consent) ? <PhotographyConsentCheckInAlert /> : null}
      <div style={scanModalOutcomeBoxStyle(tone, 600)}>
        Do you want to allow this waitlisted attendee to check in?
      </div>
    </div>
  );
}

function getPotentialStatusLabel(p) {
  const att = (p?.attendance || "").trim();
  return att === "Waitlisted" ? "Waitlist" : "Pending";
}

function getCheckedInStatusLabel(p) {
  const att = (p?.attendance || "").trim();
  if (att === "Walk-In") return "Walk-In";
  if (att === "Attended") return "Checked In";
  return att || "—";
}

function getNoShowStatusLabel(p) {
  const att = (p?.attendance || "").trim();
  return att === "No-Show" ? "No-Show" : att || "—";
}

function nextSortConfig(prev, key) {
  const prevKey = prev?.key || null;
  const prevDir = prev?.direction === "desc" ? "desc" : "asc";
  const direction = prevKey === key && prevDir === "asc" ? "desc" : "asc";
  return { key, direction };
}

/** True while a check-in dialog (or in-flight scan) should ignore further QR decodes. */
function isQrScanInteractionBlocked({
  processing,
  result,
  waitlistConfirm,
  eventPickModal,
  manualWaitlistModal,
  noShowDialog,
  undoToPotentialDialog,
  detailEventId,
}) {
  if (processing) return true;
  if (result) return true;
  if (waitlistConfirm) return true;
  if (eventPickModal) return true;
  if (manualWaitlistModal) return true;
  if (noShowDialog && noShowDialog.open) return true;
  if (undoToPotentialDialog && undoToPotentialDialog.open) return true;
  if (detailEventId) return true;
  return false;
}

function EventQRCheckIn() {
  const [searchParams] = useSearchParams();
  const deepLinkEventId = (searchParams.get("eventId") || "").trim();
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo") || "{}");
  const account_id = userInfo.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const { hasPermission } = useAuth();
  const canRecordClose = hasPermission("menu.close_event_record");

  const [scanReady, setScanReady] = useState(false);
  const [scanError, setScanError] = useState("");
  const [result, setResult] = useState(null);
  /** When set, shows confirm modal before calling mark for waitlisted participants. */
  const [waitlistConfirm, setWaitlistConfirm] = useState(null);
  /** When a scanned code matches more than one event today, staff picks the session here. */
  const [eventPickModal, setEventPickModal] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [capacityBumpBusy, setCapacityBumpBusy] = useState(false);
  const [capacityBumpError, setCapacityBumpError] = useState(null);
  const [capacityBumpDelta, setCapacityBumpDelta] = useState(1);
  const lastScannedIdRef = useRef(null);
  const lastScannedTimeRef = useRef(0);
  /**
   * Camera keeps decoding while modals are open; ignore those frames until staff dismisses
   * (avoids scan → success → same QR → "already checked in" / error swap before OK).
   * Updated synchronously when a scan starts so React state lag cannot admit a second scan.
   */
  const scanInteractionBlockedRef = useRef(false);
  const scannerRef = useRef(null);
  /** Set while `scanner.start()` is in flight so cleanup can stop before ref is assigned. */
  const scannerPendingRef = useRef(null);
  /**
   * Resolves when the current QR Check-In camera pipeline settles (getCameras + start, or error).
   * While `start()` is in flight, html5-qrcode still reports NOT_STARTED, so we must await this before
   * hiding the tab or `stop()` is skipped and `video.play()` can reject (Chrome "play() interrupted").
   */
  const scannerLifecyclePromiseRef = useRef(Promise.resolve());
  const loadParticipantsRef = useRef(async () => {});
  const beginParticipantsRequest = useLatestRequestSeq();

  const [openEvents, setOpenEvents] = useState([]);
  const [openEventsLoading, setOpenEventsLoading] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState("");
  const selectedEventIdRef = useRef(selectedEventId);
  const [participants, setParticipants] = useState([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [guestSearch, setGuestSearch] = useState("");
  const [checkedInSearch, setCheckedInSearch] = useState("");
  const [noShowSearch, setNoShowSearch] = useState("");
  const [detailEventId, setDetailEventId] = useState(null);
  const eventsBootstrappedRef = useRef(false);
  /** Selected rows on Pending Check-In (participant _id). */
  const [selectedPotentialIds, setSelectedPotentialIds] = useState(() => new Set());
  /** Manual queue: same payload as QR waitlistConfirm + ids still to process after confirm. */
  const [manualWaitlistModal, setManualWaitlistModal] = useState(null);
  const manualCheckInBatchIdsRef = useRef(null);
  /** Remaining Pending Check-In ids after a success modal is dismissed (bulk manual check-in). */
  const manualCheckInResumeQueueRef = useRef(null);
  /** Confirm marking registered guest(s) as No-Show (aligned with Record & Close Event). */
  const [noShowDialog, setNoShowDialog] = useState({ open: false, participantIds: [], message: "" });
  /** Confirm undo: move participant back to Pending Check-In by setting status to Registered. */
  const [undoToPotentialDialog, setUndoToPotentialDialog] = useState({ open: false, participantId: "", message: "" });
  const [potentialBulkBusy, setPotentialBulkBusy] = useState(false);
  const [undoBusy, setUndoBusy] = useState(false);
  const [potentialSortConfig, setPotentialSortConfig] = useState({ key: null, direction: "asc" });
  const [checkedInSortConfig, setCheckedInSortConfig] = useState({ key: null, direction: "asc" });
  const [noShowSortConfig, setNoShowSortConfig] = useState({ key: null, direction: "asc" });
  /** qr_walkin = camera + walk-in; potential / checkedin / noshow = guest lists (event + KPIs stay above tabs) */
  const [eventQrTab, setEventQrTab] = useState("qr_walkin");
  /** QR Check-In: camera / QR decode only runs when checked (saves battery and avoids stray play() races). */
  const [liveQrCameraEnabled, setLiveQrCameraEnabled] = useState(false);

  useEffect(() => {
    selectedEventIdRef.current = selectedEventId;
  }, [selectedEventId]);

  useEffect(() => {
    scanInteractionBlockedRef.current = isQrScanInteractionBlocked({
      processing,
      result,
      waitlistConfirm,
      eventPickModal,
      manualWaitlistModal,
      noShowDialog,
      undoToPotentialDialog,
      detailEventId,
    });
  }, [
    processing,
    result,
    waitlistConfirm,
    eventPickModal,
    manualWaitlistModal,
    noShowDialog,
    undoToPotentialDialog,
    detailEventId,
  ]);

  /** Stop the QR scanner before React paints `hidden` on the reader (avoids video play() interrupted). */
  const handleEventQrTabChange = useCallback(
    async (nextId) => {
      if (eventQrTab === "qr_walkin" && nextId !== "qr_walkin") {
        await awaitScannerLifecycleSafe(scannerLifecyclePromiseRef.current);
        try {
          stopHtml5QrcodeScannerAndClearRef(scannerRef, scannerPendingRef);
        } catch {
          /* covered by util; extra guard for overlay */
        }
        setScanReady(false);
      }
      if (eventQrTab === "record_close" && nextId !== "record_close" && selectedEventId) {
        void loadParticipantsRef.current(selectedEventId);
      }
      setEventQrTab(nextId);
    },
    [eventQrTab, selectedEventId]
  );

  useEffect(() => installHtml5QrcodeBenignRejectionGuard(), []);

  const SCAN_COOLDOWN_MS = 2500;

  const loadParticipants = useCallback(
    async (eventIdOverride) => {
      const eid =
        eventIdOverride != null && String(eventIdOverride).trim() !== ""
          ? String(eventIdOverride).trim()
          : selectedEventId;
      if (!eid || !account_id) {
        if (eventIdOverride == null) setParticipants([]);
        return;
      }
      const req = beginParticipantsRequest();
      setParticipantsLoading(true);
      try {
        const resp = await apiFetch(`${serverUrl}/getEventParticipants`, {
          method: "POST",
          body: JSON.stringify({ account_id, event_id: eid }),
        });
        if (!req.isCurrent()) return;
        if (!resp.ok) {
          setParticipants([]);
          return;
        }
        const data = await resp.json();
        if (!req.isCurrent()) return;
        setParticipants(Array.isArray(data) ? data : []);
      } catch {
        if (!req.isCurrent()) return;
        setParticipants([]);
      } finally {
        if (!req.isCurrent()) return;
        setParticipantsLoading(false);
      }
    },
    [account_id, serverUrl, selectedEventId, beginParticipantsRequest]
  );

  useEffect(() => {
    loadParticipantsRef.current = loadParticipants;
  }, [loadParticipants]);

  const runCheckInContinuationRef = useRef(async () => {});

  const finishManualBatchOnSuccess = useCallback((restQueue) => {
    if (restQueue && restQueue.length > 0) {
      manualCheckInResumeQueueRef.current = restQueue;
      setPotentialBulkBusy(false);
      return;
    }
    if (manualCheckInBatchIdsRef.current) {
      const batch = manualCheckInBatchIdsRef.current;
      manualCheckInBatchIdsRef.current = null;
      setSelectedPotentialIds((prev) => {
        const n = new Set(prev);
        batch.forEach((id) => n.delete(id));
        return n;
      });
    }
    setPotentialBulkBusy(false);
  }, []);

  const failManualBatch = useCallback(() => {
    manualCheckInBatchIdsRef.current = null;
    setPotentialBulkBusy(false);
  }, []);

  const buildCheckInFlowCtx = useCallback(
    (manualBatch) => ({
      serverUrl,
      accountId: account_id,
      getSelectedEventId: () => selectedEventIdRef.current,
      setSelectedEventId,
      setResult,
      setWaitlistConfirm,
      loadParticipantsFn: loadParticipantsRef.current,
      manualBatch,
    }),
    [account_id, serverUrl]
  );

  const runCheckInContinuation = useCallback(
    async (pre, manualBatch) => {
      if (pre.kind === "pick_event") return { done: true };
      return continueCheckInFromPreflight(pre, buildCheckInFlowCtx(manualBatch));
    },
    [buildCheckInFlowCtx]
  );

  runCheckInContinuationRef.current = runCheckInContinuation;

  const handleEventPickChoice = useCallback(
    async (participantId) => {
      const pid = participantId != null ? String(participantId).trim() : "";
      if (!pid) return;
      setEventPickModal(null);
      setProcessing(true);
      setResult(null);
      setWaitlistConfirm(null);
      try {
        const pre = await evaluateParticipantCheckInPreflight(serverUrl, account_id, pid, {
          fromEventParticipant: true,
        });
        await runCheckInContinuationRef.current(pre);
      } catch (err) {
        setResult({ error: err.message || "Something went wrong." });
      } finally {
        setProcessing(false);
      }
    },
    [account_id, serverUrl]
  );

  const loadOpenEvents = useCallback(async () => {
    if (!account_id) return;
    setOpenEventsLoading(true);
    try {
      const r = await apiFetch(`${serverUrl}/getOpenEvents`, {
        method: "POST",
        body: JSON.stringify({ account_id, include_private: true }),
      });
      const data = await r.json().catch(() => []);
      setOpenEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      setOpenEvents([]);
      showResultModal(setResult, {
        title: "Unable to load events",
        message: getMessageForFetchError(err),
      });
    } finally {
      setOpenEventsLoading(false);
    }
  }, [account_id, serverUrl]);

  const handleRecordCloseFinished = useCallback(async () => {
    eventsBootstrappedRef.current = false;
    setSelectedEventId("");
    setParticipants([]);
    await loadOpenEvents();
  }, [loadOpenEvents]);

  useEffect(() => {
    void loadOpenEvents();
  }, [loadOpenEvents]);

  useEffect(() => {
    eventsBootstrappedRef.current = false;
  }, [account_id]);

  const upcomingOpenEvents = useMemo(() => getUpcomingEventsSorted(openEvents), [openEvents]);

  useEffect(() => {
    if (upcomingOpenEvents.length === 0) return;
    if (deepLinkEventId) {
      const match = upcomingOpenEvents.find((e) => String(e._id) === deepLinkEventId);
      if (match) {
        setSelectedEventId(deepLinkEventId);
        eventsBootstrappedRef.current = true;
        return;
      }
    }
    if (eventsBootstrappedRef.current) return;
    const pick = pickDefaultUpcomingEvent(upcomingOpenEvents);
    if (pick && pick._id) {
      setSelectedEventId(String(pick._id));
      eventsBootstrappedRef.current = true;
    }
  }, [upcomingOpenEvents, deepLinkEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    const action = getFilterSelectionValidityAction({
      selectedId: selectedEventId,
      optionList: upcomingOpenEvents,
      optionsReady: !openEventsLoading,
      allValue: "",
    });
    if (action === "wait" || action === "keep") return;
    const pick = pickDefaultUpcomingEvent(upcomingOpenEvents);
    setSelectedEventId(pick && pick._id ? String(pick._id) : "");
  }, [upcomingOpenEvents, selectedEventId, openEventsLoading]);

  useEffect(() => {
    void loadParticipants();
  }, [selectedEventId, loadParticipants]);

  useEffect(() => {
    setSelectedPotentialIds(new Set());
  }, [selectedEventId]);

  const selectedEvent = useMemo(
    () => upcomingOpenEvents.find((e) => String(e._id) === String(selectedEventId)) || null,
    [upcomingOpenEvents, selectedEventId]
  );

  useEffect(() => {
    if (!result) {
      setCapacityBumpBusy(false);
      setCapacityBumpError(null);
      setCapacityBumpDelta(1);
      return;
    }
    if (result.status === "capacity_maxed") {
      const wl = getWaitlistedCountForCapacityBump(result.registration_metrics);
      setCapacityBumpDelta(clampCapacityBumpDelta(wl > 0 ? wl : 1));
      setCapacityBumpError(null);
      setCapacityBumpBusy(false);
    }
  }, [result]);

  const handleIncreaseEventCapacityFromModal = useCallback(async (delta) => {
    if (!result || result.status !== "capacity_maxed" || !result.event || !result.participant_id) return;
    const bumpDelta = clampCapacityBumpDelta(delta);
    setCapacityBumpError(null);
    setCapacityBumpBusy(true);
    const participantId = String(result.participant_id);
    try {
      const merged =
        selectedEvent && String(selectedEvent._id) === String(result.event._id)
          ? { ...result.event, ...selectedEvent }
          : result.event;
      const curMax =
        merged.max_capacity != null && Number(merged.max_capacity) > 0
          ? Math.floor(Number(merged.max_capacity))
          : result.registration_metrics?.max_capacity != null &&
              Number(result.registration_metrics.max_capacity) > 0
            ? Math.floor(Number(result.registration_metrics.max_capacity))
            : 0;
      const newMax = curMax + bumpDelta;
      const body = buildUpdateEventBodyFromEventLean(account_id, merged, newMax);
      if (!body) {
        setCapacityBumpError("Unable to update capacity: the event is missing required fields.");
        setCapacityBumpBusy(false);
        return;
      }
      const resp = await apiFetch(`${serverUrl}/updateEvent`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      const text = await resp.text();
      if (!resp.ok) {
        setCapacityBumpError(text || "Failed to update event.");
        setCapacityBumpBusy(false);
        return;
      }
      await loadOpenEvents();
      await loadParticipants(String(merged._id));
      setCapacityBumpBusy(false);
      const pre = await evaluateParticipantCheckInPreflight(serverUrl, account_id, participantId, {
        fromEventParticipant: true,
      });
      await continueCheckInFromPreflight(pre, buildCheckInFlowCtx());
    } catch (err) {
      setCapacityBumpError(getMessageForFetchError(err));
      setCapacityBumpBusy(false);
    }
  }, [result, selectedEvent, account_id, serverUrl, loadOpenEvents, loadParticipants, buildCheckInFlowCtx]);

  const dashboardMetrics = useMemo(
    () => computeRegistrationMetrics(selectedEvent, participants),
    [selectedEvent, participants]
  );

  /** Registered (slot held, not checked in) and Waitlisted guests for the selected event. */
  const checkInTableRows = useMemo(() => {
    const q = (guestSearch || "").trim().toLowerCase();
    const rows = (participants || []).filter((p) => {
      const a = (p.attendance || "").trim();
      return a === "Registered" || a === "Waitlisted";
    });
    const filtered = q
      ? rows.filter((p) => {
          const name = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
          const emailRaw = (p.email || "").trim();
          const email = emailRaw && !isPlaceholderEmail(emailRaw) ? emailRaw.toLowerCase() : "";
          const phone = (p.phone || "").toLowerCase();
          const status = (p.attendance || "").trim().toLowerCase();
          if (name.includes(q) || email.includes(q) || phone.includes(q)) return true;
          if (status.includes(q)) return true;
          if (q.includes("wait") && status === "waitlisted") return true;
          if ((q.includes("register") || q === "reg") && status === "registered") return true;
          if (getPhotographyConsentLabel(p.photography_consent).toLowerCase().includes(q)) return true;
          return false;
        })
      : rows;

    const normalized = filtered.map((p) => ({
      ...buildParticipantSortRow(p),
      reg_type: getParticipantRegTypeCell(p, selectedEvent),
      status_label: getPotentialStatusLabel(p),
    }));
    const keyTypes = {
      participant_name: "string",
      registered_at: "date",
      reg_type: "string",
      email: "string",
      phone: "string",
      status_label: "string",
      photo_consent_sort: "string",
    };
    if (!potentialSortConfig?.key) {
      return sortRowsByConfig(normalized, { key: "participant_name", direction: "asc" }, keyTypes);
    }
    return sortRowsByConfig(normalized, potentialSortConfig, keyTypes);
  }, [participants, guestSearch, potentialSortConfig, selectedEvent]);

  /** Attended and walk-in participants (already checked in / present). */
  const checkedInTableRows = useMemo(() => {
    const q = (checkedInSearch || "").trim().toLowerCase();
    const rows = (participants || []).filter((p) => {
      const a = (p.attendance || "").trim();
      return a === "Attended" || a === "Walk-In";
    });
    const filtered = q
      ? rows.filter((p) => {
          const name = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
          const emailRaw = (p.email || "").trim();
          const email = emailRaw && !isPlaceholderEmail(emailRaw) ? emailRaw.toLowerCase() : "";
          const phone = (p.phone || "").toLowerCase();
          const status = (p.attendance || "").trim().toLowerCase();
          if (name.includes(q) || email.includes(q) || phone.includes(q)) return true;
          if (status.includes(q)) return true;
          if (
            (q.includes("attend") ||
              q.includes("checked in") ||
              q.includes("check in") ||
              q === "checked") &&
            (status === "attended" || status === "walk-in")
          )
            return true;
          if (q.includes("walk") && status === "walk-in") return true;
          if (getPhotographyConsentLabel(p.photography_consent).toLowerCase().includes(q)) return true;
          return false;
        })
      : rows;

    const normalized = filtered.map((p) => ({
      ...buildParticipantSortRow(p),
      reg_type: getParticipantRegTypeCell(p, selectedEvent),
      status_label: getCheckedInStatusLabel(p),
    }));
    const keyTypes = {
      participant_name: "string",
      registered_at: "date",
      reg_type: "string",
      email: "string",
      phone: "string",
      status_label: "string",
      photo_consent_sort: "string",
    };
    if (!checkedInSortConfig?.key) {
      return sortRowsByConfig(normalized, { key: "participant_name", direction: "asc" }, keyTypes);
    }
    return sortRowsByConfig(normalized, checkedInSortConfig, keyTypes);
  }, [participants, checkedInSearch, checkedInSortConfig, selectedEvent]);

  /** Participants marked No-Show (had a spot, did not attend). */
  const noShowTableRows = useMemo(() => {
    const q = (noShowSearch || "").trim().toLowerCase();
    const rows = (participants || []).filter((p) => (p.attendance || "").trim() === "No-Show");
    const filtered = q
      ? rows.filter((p) => {
          const name = `${p.first_name || ""} ${p.last_name || ""}`.toLowerCase();
          const emailRaw = (p.email || "").trim();
          const email = emailRaw && !isPlaceholderEmail(emailRaw) ? emailRaw.toLowerCase() : "";
          const phone = (p.phone || "").toLowerCase();
          const status = (p.attendance || "").trim().toLowerCase();
          if (name.includes(q) || email.includes(q) || phone.includes(q)) return true;
          if (status.includes(q)) return true;
          if ((q.includes("no-show") || q.includes("noshow") || q.includes("no show")) && status === "no-show") return true;
          if (getPhotographyConsentLabel(p.photography_consent).toLowerCase().includes(q)) return true;
          return false;
        })
      : rows;

    const normalized = filtered.map((p) => ({
      ...buildParticipantSortRow(p),
      reg_type: getParticipantRegTypeCell(p, selectedEvent),
      status_label: getNoShowStatusLabel(p),
    }));
    const keyTypes = {
      participant_name: "string",
      registered_at: "date",
      reg_type: "string",
      email: "string",
      phone: "string",
      status_label: "string",
      photo_consent_sort: "string",
    };
    if (!noShowSortConfig?.key) {
      return sortRowsByConfig(normalized, { key: "participant_name", direction: "asc" }, keyTypes);
    }
    return sortRowsByConfig(normalized, noShowSortConfig, keyTypes);
  }, [participants, noShowSearch, noShowSortConfig, selectedEvent]);

  const handlePotentialSort = useCallback((key) => setPotentialSortConfig((prev) => nextSortConfig(prev, key)), []);
  const handleCheckedInSort = useCallback((key) => setCheckedInSortConfig((prev) => nextSortConfig(prev, key)), []);
  const handleNoShowSort = useCallback((key) => setNoShowSortConfig((prev) => nextSortConfig(prev, key)), []);

  /** Totals for tab labels (not search-filtered). */
  const eventQrTabCounts = useMemo(() => {
    const rows = Array.isArray(participants) ? participants : [];
    let potential = 0;
    let checkedin = 0;
    let noshow = 0;
    for (let i = 0; i < rows.length; i += 1) {
      const a = (rows[i].attendance || "").trim();
      if (a === "Registered" || a === "Waitlisted") potential += 1;
      else if (a === "Attended" || a === "Walk-In") checkedin += 1;
      else if (a === "No-Show") noshow += 1;
    }
    return {
      qr_walkin: rows.length,
      potential,
      checkedin,
      noshow,
    };
  }, [participants]);

  /** Same pipeline as QR: preflight → shared continueCheckInFromPreflight. */
  const executeManualCheckInQueue = useCallback(
    async (ids) => {
      const queue = (ids || []).map((x) => String(x).trim()).filter(Boolean);
      if (queue.length === 0) {
        setPotentialBulkBusy(false);
        if (manualCheckInBatchIdsRef.current) {
          const batch = manualCheckInBatchIdsRef.current;
          manualCheckInBatchIdsRef.current = null;
          setSelectedPotentialIds((prev) => {
            const n = new Set(prev);
            batch.forEach((id) => n.delete(id));
            return n;
          });
        }
        return;
      }
      if (!manualCheckInBatchIdsRef.current) {
        manualCheckInBatchIdsRef.current = queue.slice();
      }
      setPotentialBulkBusy(true);
      try {
        const pid = queue[0];
        const pre = await evaluateParticipantCheckInPreflight(serverUrl, account_id, pid, {
          fromEventParticipant: true,
        });
        const outcome = await runCheckInContinuation(pre, {
          resumeQueue: queue.slice(1),
          onBatchFail: failManualBatch,
          onMarkSuccess: finishManualBatchOnSuccess,
        });
        if (outcome?.manualWaitlist) {
          setManualWaitlistModal(outcome.manualWaitlist);
        }
      } catch (err) {
        failManualBatch();
        showResultModal(setResult, { title: "Check-in not completed", message: getMessageForFetchError(err) });
      }
    },
    [account_id, serverUrl, runCheckInContinuation, failManualBatch, finishManualBatchOnSuccess]
  );

  const executeManualCheckInQueueRef = useRef(executeManualCheckInQueue);
  executeManualCheckInQueueRef.current = executeManualCheckInQueue;

  const beginManualCheckIn = useCallback(
    (ids) => {
      const idList = (ids || []).map((x) => String(x).trim()).filter(Boolean);
      if (idList.length === 0) return;
      manualCheckInBatchIdsRef.current = null;
      void executeManualCheckInQueue(idList);
    },
    [executeManualCheckInQueue]
  );

  const runMarkNoShowForParticipants = useCallback(
    async (ids) => {
      const idList = (ids || []).map((x) => String(x).trim()).filter(Boolean);
      if (idList.length === 0) return;
      setPotentialBulkBusy(true);
      try {
        for (const pid of idList) {
          const res = await postMarkParticipantNoShow(serverUrl, account_id, pid);
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            showResultModal(setResult, {
              title: "No-show not completed",
              message: errData.error || "Failed to mark no-show.",
            });
            await loadParticipants(selectedEventId);
            return;
          }
        }
        await loadParticipants(selectedEventId);
        setSelectedPotentialIds((prev) => {
          const n = new Set(prev);
          idList.forEach((id) => n.delete(id));
          return n;
        });
      } catch (err) {
        showResultModal(setResult, { title: "No-show not completed", message: getMessageForFetchError(err) });
      } finally {
        setPotentialBulkBusy(false);
      }
    },
    [account_id, serverUrl, loadParticipants, selectedEventId]
  );

  const requestNoShow = useCallback(
    (ids) => {
      const idList = (ids || []).map((x) => String(x).trim()).filter(Boolean);
      if (idList.length === 0) return;
      const registeredIds = idList.filter((id) => {
        const p = participants.find((x) => String(x._id) === String(id));
        return p && (p.attendance || "").trim() === "Registered";
      });
      if (registeredIds.length === 0) {
        showResultModal(setResult, {
          title: "No-show not available",
          message:
            "No-show applies to registered guests with a spot (same as Record & Close Event). For waitlisted guests, use Check in or cancel registration.",
        });
        return;
      }
      const skipped = idList.length - registeredIds.length;
      const msg =
        registeredIds.length === 1
          ? "Confirm no-show for this participant? They held a registration spot but did not attend."
          : `Confirm no-show for ${registeredIds.length} participant(s)? They held a registration spot but did not attend.${
              skipped > 0 ? ` ${skipped} selected guest(s) are not registered (waitlisted or other) and will be skipped.` : ""
            }`;
      setNoShowDialog({ open: true, participantIds: registeredIds, message: msg });
    },
    [participants]
  );

  const requestUndoToPotential = useCallback(
    (participantId) => {
      const pid = participantId != null ? String(participantId).trim() : "";
      if (!pid) return;
      const p = participants.find((x) => String(x._id) === String(pid));
      const name = p ? [p.first_name, p.last_name].filter(Boolean).join(" ").trim() : "";
      const att = p ? String(p.attendance || "").trim() : "";
      const msg = `Move ${name || "this participant"} back to Pending Check-In?\n\nThis will undo "${att || "current status"}" and set status to "Registered".`;
      setUndoToPotentialDialog({ open: true, participantId: pid, message: msg });
    },
    [participants]
  );

  const runUndoToPotential = useCallback(
    async (participantId) => {
      const pid = participantId != null ? String(participantId).trim() : "";
      if (!pid) return;
      setUndoBusy(true);
      try {
        const res = await postMarkParticipantRegistered(serverUrl, account_id, pid);
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          showResultModal(setResult, {
            title: "Undo not completed",
            message: errData.error || "Failed to move participant back to Pending Check-In.",
          });
          return;
        }
        await loadParticipants(selectedEventId);
        setEventQrTab("potential");
      } catch (err) {
        showResultModal(setResult, { title: "Undo not completed", message: getMessageForFetchError(err) });
      } finally {
        setUndoBusy(false);
      }
    },
    [account_id, serverUrl, loadParticipants, selectedEventId]
  );

  useLayoutEffect(() => {
    if (!account_id || eventQrTab !== "qr_walkin" || !liveQrCameraEnabled) {
      const prevLifecycle = scannerLifecyclePromiseRef.current;
      try {
        stopHtml5QrcodeScannerAndClearRef(scannerRef, scannerPendingRef);
      } catch {
        /* util already try/catch; guard effect body */
      }
      scannerLifecyclePromiseRef.current = chainStopAfterLifecycle(prevLifecycle, () => {
        try {
          stopHtml5QrcodeScannerAndClearRef(scannerRef, scannerPendingRef);
        } catch {
          /* exhaustive */
        }
      });
      setScanReady(false);
      if (eventQrTab === "qr_walkin" && !liveQrCameraEnabled) {
        setScanError("");
      }
      return undefined;
    }

    const onSuccess = async (decodedText) => {
      const id = String(decodedText).trim();
      if (!id) return;
      // Hold while any check-in dialog is open (or a scan is already in flight).
      if (scanInteractionBlockedRef.current) return;
      const now = Date.now();
      if (id === lastScannedIdRef.current && now - lastScannedTimeRef.current < SCAN_COOLDOWN_MS) {
        return;
      }
      lastScannedIdRef.current = id;
      lastScannedTimeRef.current = now;
      // Lock immediately — React state updates are too late to stop the next camera frame.
      scanInteractionBlockedRef.current = true;

      setProcessing(true);
      setResult(null);
      setWaitlistConfirm(null);

      try {
        const pre = await evaluateParticipantCheckInPreflight(serverUrl, account_id, id);
        if (pre.kind === "pick_event") {
          setEventPickModal({ choices: pre.choices });
          setProcessing(false);
          return;
        }
        await runCheckInContinuationRef.current(pre);
      } catch (err) {
        setResult({ error: err.message || "Something went wrong." });
      } finally {
        setProcessing(false);
      }
    };

    // Prefer an explicit device id from getCameras() — facingMode:"environment" often fails on desktops
    // (and picking the last unlabeled device often selects Windows IR → silent black preview).
    const config = { fps: 10 };

    let cancelled = false;

    const startScannerWithCameraFallback = async (cameraIds) => {
      let lastErr = null;
      for (let i = 0; i < cameraIds.length; i += 1) {
        if (cancelled) return;
        const scanner = new Html5Qrcode(QR_READER_ELEMENT_ID);
        scannerPendingRef.current = scanner;
        try {
          await scanner.start(cameraIds[i], config, onSuccess, () => {});
          scannerPendingRef.current = null;
          if (cancelled) {
            try {
              stopHtml5QrcodeScannerAndClearRef({ current: scanner }, null);
            } catch {
              /* exhaustive */
            }
            return;
          }
          scannerRef.current = scanner;
          setScanReady(true);
          return;
        } catch (startErr) {
          lastErr = startErr;
          scannerPendingRef.current = null;
          try {
            tryStopHtml5QrcodeInstance(scanner);
          } catch {
            /* exhaustive */
          }
          try {
            if (typeof scanner.clear === "function") scanner.clear();
          } catch {
            /* element may already be empty */
          }
          if (cancelled || isBenignHtml5CameraTeardownError(startErr)) {
            return;
          }
        }
      }
      if (cancelled) return;
      if (lastErr && !isBenignHtml5CameraTeardownError(lastErr)) {
        setScanError(getHtml5QrcodeCameraErrorMessage(lastErr));
      } else if (!lastErr) {
        setScanError("Could not start camera.");
      }
      setScanReady(true);
    };

    const lifecyclePromise = Html5Qrcode.getCameras()
      .then((cameras) => {
        if (cancelled) return undefined;
        const cameraIds = listPreferredQrCameraIds(cameras);
        if (!cameraIds.length) {
          setScanError("No camera found. Connect a camera and turn scanning on again.");
          setScanReady(true);
          return undefined;
        }
        return startScannerWithCameraFallback(cameraIds);
      })
      .catch((err) => {
        if (cancelled) return;
        if (isBenignHtml5CameraTeardownError(err)) return;
        setScanError(getHtml5QrcodeCameraErrorMessage(err));
        setScanReady(true);
      });

    scannerLifecyclePromiseRef.current = lifecyclePromise.catch(() => {});

    return () => {
      cancelled = true;
      void awaitScannerLifecycleSafe(scannerLifecyclePromiseRef.current).then(() => {
        try {
          stopHtml5QrcodeScannerAndClearRef(scannerRef, scannerPendingRef);
        } catch {
          /* exhaustive */
        }
        setScanReady(false);
      });
    };
  }, [account_id, serverUrl, eventQrTab, liveQrCameraEnabled]);

  return (
    <div className="close-event-record event-qr-page page-container page-container-full page-container--section-tabs">
      <SectionTabsPageHeader subtitle="Choose the event and review fill rates at the top. Use QR Check-In for the check-in camera, Pending Check-In for guests not yet checked in, Checked-In for those who checked in, and No-Show for participants marked no-show.">
        <EventSectionTabs activeTab={EVENT_TAB_CHECKIN} />
      </SectionTabsPageHeader>
      <div className="page-content" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <EventDetailModal
          eventId={detailEventId}
          open={Boolean(detailEventId)}
          onClose={() => setDetailEventId(null)}
          returnTo="/EventQRCheckIn"
        />
        <ConfirmationModal
          open={Boolean(manualWaitlistModal)}
          title="Waitlisted participant"
          message={
            manualWaitlistModal ? (
              <WaitlistConfirmMessage
                participantName={manualWaitlistModal.waitlistConfirm.participantName}
                eventName={manualWaitlistModal.waitlistConfirm.eventName}
                eventDateTime={manualWaitlistModal.waitlistConfirm.eventDateTime}
                photography_consent={manualWaitlistModal.waitlistConfirm.photography_consent}
              />
            ) : null
          }
          mode="confirm"
          confirmLabel="Allow check-in"
          cancelLabel="Don't allow"
          onConfirm={async () => {
            const m = manualWaitlistModal;
            setManualWaitlistModal(null);
            if (!m?.waitlistConfirm) return;
            const p = m.waitlistConfirm;
            setPotentialBulkBusy(true);
            try {
              await completeWaitlistAllowCheckIn(p, buildCheckInFlowCtx(), {
                manualBatch: {
                  resumeQueue: m.resumeQueue || [],
                  onBatchFail: failManualBatch,
                  onMarkSuccess: finishManualBatchOnSuccess,
                },
              });
            } catch (err) {
              failManualBatch();
              setResult({
                participantName: p.participantName,
                eventName: p.eventName,
                eventDateTime: p.eventDateTime,
                status: "error",
                error: getMessageForFetchError(err),
                registration_metrics: p.registration_metrics,
                photography_consent: p.photography_consent,
              });
            }
          }}
          onCancel={() => {
            setManualWaitlistModal(null);
            manualCheckInBatchIdsRef.current = null;
            setPotentialBulkBusy(false);
            setResult({ status: "waitlist_declined" });
          }}
        />

        <ConfirmationModal
          open={noShowDialog.open}
          title="No-show"
          message={noShowDialog.message}
          mode="confirm"
          confirmLabel="No-show"
          cancelLabel="Cancel"
          onConfirm={async () => {
            const ids = noShowDialog.participantIds;
            setNoShowDialog({ open: false, participantIds: [], message: "" });
            if (ids.length > 0) {
              await runMarkNoShowForParticipants(ids);
            }
          }}
          onCancel={() => setNoShowDialog({ open: false, participantIds: [], message: "" })}
        />

        <ConfirmationModal
          open={undoToPotentialDialog.open}
          title="Move back to Pending Check-In"
          message={undoToPotentialDialog.message}
          mode="confirm"
          confirmLabel="Move to Pending Check-In"
          cancelLabel="Cancel"
          confirmDisabled={undoBusy}
          onConfirm={async () => {
            const pid = undoToPotentialDialog.participantId;
            setUndoToPotentialDialog({ open: false, participantId: "", message: "" });
            if (pid) await runUndoToPotential(pid);
          }}
          onCancel={() => setUndoToPotentialDialog({ open: false, participantId: "", message: "" })}
        />

        {scanError && (
          <div
            style={{
              padding: "10px 12px",
              marginBottom: "10px",
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c2c7",
              borderRadius: "6px",
              color: "#842029",
              fontSize: "13px",
            }}
          >
            {scanError}
          </div>
        )}

        <div className="event-qr-tabbed" style={{ marginTop: "4px", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <div className="event-qr-event-metrics-block">
            <div className="panel event-qr-panel-event-metrics">
              <div
                className={
                  selectedEventId && dashboardMetrics
                    ? "event-qr-metrics-head event-qr-metrics-head--inline-kpis"
                    : "event-qr-metrics-head"
                }
              >
                <div
                  className="panel-row event-qr-event-toolbar"
                  style={{
                    marginBottom: selectedEventId && dashboardMetrics ? 0 : "10px",
                    flexWrap: "wrap",
                    gap: "8px",
                    alignItems: "center",
                  }}
                >
                  <span className="toolbar-label">Event</span>
                  <select
                    value={selectedEventId}
                    onChange={(e) => setSelectedEventId(e.target.value)}
                    className="input"
                    style={{ flex: "1 1 220px", minWidth: "160px" }}
                    disabled={openEventsLoading}
                  >
                    <option value="">{openEventsLoading ? "Loading events…" : "Select an open event…"}</option>
                    {(upcomingOpenEvents || []).map((ev) => (
                      <option key={ev._id} value={String(ev._id)}>
                        {ev.name || "Event"}
                        {ev.start_datetime ? ` · ${dayjs(ev.start_datetime).format("MMM D, YYYY")}` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedEventId && dashboardMetrics ? (
                  <EventRegistrationKpiCards metrics={dashboardMetrics} />
                ) : null}
              </div>
              {!selectedEventId ? (
                <p className="panel-hint" style={{ margin: 0 }}>
                  Choose an event to load fill rate, turnout, and spillover. Scanning a participant check-in code on{" "}
                  <strong>QR Check-In</strong> can also select the event.
                </p>
              ) : null}
            </div>
          </div>

          <div className="tabs-bar" role="tablist" aria-label="Check-in workspace">
            <div className="tabs-list">
              {[
                { id: "qr_walkin", label: "QR Check-In" },
                { id: "potential", label: "Pending Check-In" },
                { id: "checkedin", label: "Checked-In" },
                { id: "noshow", label: "No-Show" },
                ...(canRecordClose ? [{ id: "record_close", label: "Record & Close" }] : []),
              ].map((t) => {
                const n = eventQrTabCounts[t.id];
                const showCount = t.id !== "qr_walkin" && t.id !== "record_close";
                const labelWithCount = showCount ? `${t.label} (${n})` : t.label;
                const ariaLabel = showCount
                  ? `${t.label}, ${n} ${n === 1 ? "record" : "records"}`
                  : t.label;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={eventQrTab === t.id}
                    aria-label={ariaLabel}
                    className={`tab-btn${eventQrTab === t.id ? " active" : ""}`}
                    onClick={() => handleEventQrTabChange(t.id)}
                  >
                    {labelWithCount}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="tab-content event-qr-tab-panels">
            {/*
              Keep this panel mounted when switching tabs so the reader host is not removed while html5-qrcode /
              video.play() is still starting (avoids "play() interrupted" errors).
            */}
            <div
              className="event-qr-tab-panel event-qr-tab-panel--qr-walkin"
              role="tabpanel"
              id="event-qr-panel-qr-walkin"
              hidden={eventQrTab !== "qr_walkin"}
              aria-hidden={eventQrTab !== "qr_walkin"}
            >
              <div className="event-qr-checkin-walkin-split">
                <div className="event-qr-split-col event-qr-split-col--qr">
                  <div className="panel event-qr-panel-checkin" style={{ minHeight: 0 }}>
                    <div className="event-qr-scan-header">
                      <div className="panel-title">QR Check-In</div>
                      <p className="panel-hint" style={{ margin: 0 }}>
                        Point the camera at a participant&apos;s check-in QR code to complete attendance when eligible.
                      </p>
                    </div>
                    <label className="event-qr-live-camera-toggle" htmlFor="event-qr-live-camera-toggle">
                      <input
                        id="event-qr-live-camera-toggle"
                        type="checkbox"
                        checked={liveQrCameraEnabled}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setLiveQrCameraEnabled(on);
                          if (on) setScanError("");
                        }}
                      />
                      <span>Turn on camera to scan check-in QR codes</span>
                    </label>
                    <div className="event-qr-reader-wrap">
                      <div
                        id={QR_READER_ELEMENT_ID}
                        className={`event-qr-reader-host${!liveQrCameraEnabled ? " event-qr-reader-host--inactive" : ""}`}
                      />
                      {!liveQrCameraEnabled && (
                        <p className="event-qr-camera-off-hint">Camera is off. Enable it when you are ready to scan.</p>
                      )}
                      {liveQrCameraEnabled && !scanReady && !scanError && (
                        <p className="event-qr-camera-starting-hint">Starting camera…</p>
                      )}
                      {liveQrCameraEnabled && scanError ? (
                        <p className="event-qr-camera-error-hint" role="alert">
                          {scanError}
                        </p>
                      ) : null}
                    </div>
                    {processing && (
                      <p style={{ fontSize: "13px", color: "#38bdf8", margin: "10px 0 0 0" }}>Processing scan…</p>
                    )}
                  </div>
                </div>
                <div className="event-qr-split-col event-qr-split-col--walkin">
                  <div className="panel event-qr-panel-walkin" style={{ minHeight: 0 }}>
                    <div className="panel-title">Walk-in registration</div>
                    <p className="panel-hint event-qr-panel-walkin-hint">
                      Walk-ins scan this QR to open the Event Calendar registration flow for this event.
                    </p>
                    {selectedEvent && account_id ? (
                      <div className="cards-wrapper cards-wrapper--sm event-qr-walkin-cards">
                        <article className="card EventCalendar-card--hero">
                          <div
                            className="EventCalendar-card-hero-banner"
                            style={{
                              backgroundImage: `url(${resolveEventBackgroundImageUrl(selectedEvent.background_image_url)})`,
                            }}
                            aria-hidden={true}
                          />
                          <div className="card-body">
                            <div className="card-main">
                              <div className="card-main-details">
                                <div className="card-date">
                                  <IconCalendar />
                                  {selectedEvent.start_datetime
                                    ? dayjs(selectedEvent.start_datetime).format("ddd, MMM D, YYYY")
                                    : "—"}
                                </div>
                                {selectedEvent.start_datetime && selectedEvent.end_datetime ? (
                                  <div className="card-time">
                                    {dayjs(selectedEvent.start_datetime).format("h:mm A")} –{" "}
                                    {dayjs(selectedEvent.end_datetime).format("h:mm A")}
                                  </div>
                                ) : null}
                                <button
                                  type="button"
                                  className="btn-link card-title"
                                  onClick={() => setDetailEventId(selectedEvent?._id ? String(selectedEvent._id) : null)}
                                  aria-label={`Open details for ${selectedEvent.name || "event"}`}
                                  title="Open event details"
                                >
                                  {selectedEvent.name || "Event"}
                                </button>
                                {selectedEvent.organizer_name ? (
                                  <div className="card-meta">
                                    <span className="card-meta-label">Hosted By:</span>
                                    <span>{selectedEvent.organizer_name}</span>
                                  </div>
                                ) : null}
                                <EventCardPriceMeta event={selectedEvent} />
                                {selectedEvent.location && String(selectedEvent.location).trim() ? (
                                  <div className="card-location" title={String(selectedEvent.location).trim()}>
                                    <IconLocation />
                                    <span>{String(selectedEvent.location).trim()}</span>
                                  </div>
                                ) : null}
                                <EventDescriptionPreview
                                  description={selectedEvent.description}
                                  dialogTitle={
                                    selectedEvent.name
                                      ? `${selectedEvent.name} — Description`
                                      : "Description"
                                  }
                                />
                              </div>
                              <div className="card-main-qr">
                                <EventRegistrationQRCode
                                  event={selectedEvent}
                                  accountId={account_id}
                                  serverUrl={serverUrl}
                                  size={96}
                                  className="card-qr-wrap"
                                />
                              </div>
                            </div>
                          </div>
                        </article>
                      </div>
                    ) : (
                      <p className="panel-hint" style={{ margin: 0 }}>
                        Select an open event above (or scan a check-in code with the camera) to show the registration QR for
                        walk-ins.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {eventQrTab === "potential" && (
              <div className="event-qr-tab-panel" role="tabpanel">
                <div className="panel" style={{ minHeight: 0 }}>
                  <div className="panel-title">Pending Check-In</div>
                  <p className="panel-hint" style={{ margin: "0 0 12px 0" }}>
                    Search and manage guests who are &quot;Registered&quot; or &quot;Waitlisted&quot; for the event selected
                    above. Changing the event reloads the list; it also updates after check-in actions.
                  </p>

                  {!selectedEventId ? (
                    <p className="panel-hint" style={{ margin: 0 }}>
                      Select an event above (or scan a check-in code on <strong>QR Check-In</strong>) to load this list.
                    </p>
                  ) : (
                    <>
                      <div className="toolbar">
                        <input
                          type="text"
                          className="input input-search-wide"
                          placeholder="Search by name, email, phone, or status (registered / waitlist)…"
                          value={guestSearch}
                          onChange={(e) => setGuestSearch(e.target.value)}
                          aria-label="Search registered and waitlisted guests"
                        />
                        <button
                          type="button"
                          onClick={() => setGuestSearch("")}
                          className="btn btn-secondary btn-sm"
                        >
                          Clear
                        </button>
                      </div>
                      <EntityListBulkToolbar
                        selectedCount={selectedPotentialIds.size}
                        ariaLabel="Bulk check-in actions"
                        actions={[
                          {
                            id: "checkin",
                            label: `Check in (${selectedPotentialIds.size})`,
                            busyLabel: "Working…",
                            busy: potentialBulkBusy,
                            className: "btn btn-primary btn-md toolbar-control",
                            disabled: participantsLoading,
                            onClick: () => beginManualCheckIn([...selectedPotentialIds]),
                          },
                          {
                            id: "noshow",
                            label: `No-show (${selectedPotentialIds.size})`,
                            className: "btn btn-secondary btn-md toolbar-control",
                            disabled: participantsLoading,
                            onClick: () => requestNoShow([...selectedPotentialIds]),
                            title: "Applies only to registered guests with a spot (same as Record & Close Event)",
                          },
                        ]}
                      />

                      <div className="event-qr-pending-scroll table-scroll">
                        {participantsLoading ? (
                          <p className="panel-hint" style={{ margin: "8px 0" }}>
                            Loading participants…
                          </p>
                        ) : checkInTableRows.length === 0 ? (
                          <p className="panel-hint" style={{ margin: "8px 0" }}>
                            No guests with &quot;Registered&quot; or &quot;Waitlisted&quot; status for this event, or no matches
                            for your search.
                          </p>
                        ) : (
                          <div className="table-wrapper">
                            <table>
                              <thead>
                                <tr>
                                  <th style={{ width: 36 }}>
                                    <input
                                      type="checkbox"
                                      checked={
                                        checkInTableRows.length > 0 &&
                                        checkInTableRows.every((row) => selectedPotentialIds.has(row._id))
                                      }
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedPotentialIds(new Set(checkInTableRows.map((row) => row._id)));
                                        } else {
                                          setSelectedPotentialIds(new Set());
                                        }
                                      }}
                                      title="Select all visible rows"
                                      aria-label="Select all visible rows"
                                    />
                                  </th>
                                  <th className="sortable" onClick={() => handlePotentialSort("participant_name")}>
                                    Participant
                                  </th>
                                  <th className="sortable" onClick={() => handlePotentialSort("registered_at")}>
                                    Registered
                                  </th>
                                  <th className="sortable" onClick={() => handlePotentialSort("reg_type")}>
                                    Reg Type
                                  </th>
                                  <th className="sortable" onClick={() => handlePotentialSort("status_label")}>
                                    Attendance
                                  </th>
                                  <th className="sortable" onClick={() => handlePotentialSort("email")}>
                                    Email
                                  </th>
                                  <th className="sortable" onClick={() => handlePotentialSort("phone")}>
                                    Phone
                                  </th>
                                  <th className="sortable" onClick={() => handlePotentialSort("photo_consent_sort")}>
                                    Photo consent
                                  </th>
                                  <th className="text-center" style={{ minWidth: 160 }}>
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {checkInTableRows.map((p) => {
                                  const att = (p.attendance || "").trim();
                                  const checkInCell = att === "Waitlisted" ? "Waitlist" : "Pending";
                                  const emailRaw = (p.email != null && String(p.email).trim()) ? String(p.email).trim() : "";
                                  const email = emailRaw && !isPlaceholderEmail(emailRaw) ? emailRaw : "";
                                  const regTypeCell = (p.reg_type != null ? String(p.reg_type) : "").trim() || "—";
                                  const rowBusy = potentialBulkBusy;
                                  return (
                                    <tr key={p._id}>
                                      <td>
                                        <input
                                          type="checkbox"
                                          checked={selectedPotentialIds.has(p._id)}
                                          onChange={() => {
                                            setSelectedPotentialIds((prev) => {
                                              const n = new Set(prev);
                                              if (n.has(p._id)) n.delete(p._id);
                                              else n.add(p._id);
                                              return n;
                                            });
                                          }}
                                          aria-label={`Select ${[p.first_name, p.last_name].filter(Boolean).join(" ") || "participant"}`}
                                        />
                                      </td>
                                      <td>{[p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "—"}</td>
                                      <td style={{ whiteSpace: "nowrap" }}>{getParticipantRegisteredAtCell(p)}</td>
                                      <td style={{ whiteSpace: "nowrap" }}>{regTypeCell}</td>
                                      <td>{checkInCell}</td>
                                      <td className="table-cell-ellipsis" title={email || undefined}>
                                        {email || "—"}
                                      </td>
                                      <td>{normalizePhoneDigits(p.phone) ? formatPhoneDisplay(p.phone) : "—"}</td>
                                      <td>
                                        <PhotographyConsentBadge consent={p.photography_consent} />
                                      </td>
                                      <td className="text-center">
                                        <RowActionIcons
                                          aria-label={`Actions for ${[p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "participant"}`}
                                        >
                                          <button
                                            type="button"
                                            className="btn-link"
                                            style={iconBtnStyle}
                                            title="Check in"
                                            aria-label="Check in"
                                            disabled={rowBusy}
                                            onClick={() => beginManualCheckIn([p._id])}
                                          >
                                            <IconApprove size={18} />
                                          </button>
                                          <button
                                            type="button"
                                            className="btn-link-muted"
                                            style={iconBtnStyle}
                                            title={
                                              att === "Registered"
                                                ? "No-show (held a spot, did not attend)"
                                                : "No-show applies only to registered guests with a spot"
                                            }
                                            aria-label="No-show"
                                            disabled={rowBusy || att !== "Registered"}
                                            onClick={() => requestNoShow([p._id])}
                                          >
                                            <IconCancel size={18} />
                                          </button>
                                        </RowActionIcons>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {eventQrTab === "checkedin" && (
              <div className="event-qr-tab-panel" role="tabpanel">
                <div className="panel" style={{ minHeight: 0 }}>
                  <div className="panel-title">Checked-In</div>
                  <p className="panel-hint" style={{ margin: "0 0 12px 0" }}>
                    Search participants who have already checked in (attendance &quot;Checked In&quot; or &quot;Walk-In&quot;) for
                    the event selected above. Changing the event reloads the list; it also updates after check-in actions.
                  </p>

                  {!selectedEventId ? (
                    <p className="panel-hint" style={{ margin: 0 }}>
                      Select an event above (or scan a check-in code on <strong>QR Check-In</strong>) to load this list.
                    </p>
                  ) : (
                    <>
                      <div className="toolbar">
                        <input
                          type="text"
                          className="input input-search-wide"
                          placeholder="Search by name, email, phone, or attendance (checked in / walk-in)…"
                          value={checkedInSearch}
                          onChange={(e) => setCheckedInSearch(e.target.value)}
                          aria-label="Search checked-in participants"
                        />
                        <button
                          type="button"
                          onClick={() => setCheckedInSearch("")}
                          className="btn btn-secondary btn-sm"
                        >
                          Clear
                        </button>
                      </div>

                      <div className="event-qr-pending-scroll table-scroll">
                        {participantsLoading ? (
                          <p className="panel-hint" style={{ margin: "8px 0" }}>
                            Loading participants…
                          </p>
                        ) : checkedInTableRows.length === 0 ? (
                          <p className="panel-hint" style={{ margin: "8px 0" }}>
                            No checked-in participants for this event, or no matches for your search.
                          </p>
                        ) : (
                          <div className="table-wrapper">
                            <table>
                              <thead>
                                <tr>
                                  <th className="sortable" onClick={() => handleCheckedInSort("participant_name")}>
                                    Participant
                                  </th>
                                  <th className="sortable" onClick={() => handleCheckedInSort("registered_at")}>
                                    Registered
                                  </th>
                                  <th className="sortable" onClick={() => handleCheckedInSort("reg_type")}>
                                    Reg Type
                                  </th>
                                  <th className="sortable" onClick={() => handleCheckedInSort("status_label")}>
                                    Attendance
                                  </th>
                                  <th className="sortable" onClick={() => handleCheckedInSort("email")}>
                                    Email
                                  </th>
                                  <th className="sortable" onClick={() => handleCheckedInSort("phone")}>
                                    Phone
                                  </th>
                                  <th className="sortable" onClick={() => handleCheckedInSort("photo_consent_sort")}>
                                    Photo consent
                                  </th>
                                  <th className="text-center" style={{ minWidth: 160 }}>
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {checkedInTableRows.map((p) => {
                                  const emailRaw = (p.email != null && String(p.email).trim()) ? String(p.email).trim() : "";
                                  const email = emailRaw && !isPlaceholderEmail(emailRaw) ? emailRaw : "";
                                  const regTypeCell = (p.reg_type != null ? String(p.reg_type) : "").trim() || "—";
                                  return (
                                    <tr key={p._id}>
                                      <td>{[p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "—"}</td>
                                      <td style={{ whiteSpace: "nowrap" }}>{getParticipantRegisteredAtCell(p)}</td>
                                      <td style={{ whiteSpace: "nowrap" }}>{regTypeCell}</td>
                                      <td>{p.status_label}</td>
                                      <td className="table-cell-ellipsis" title={email || undefined}>
                                        {email || "—"}
                                      </td>
                                      <td>{normalizePhoneDigits(p.phone) ? formatPhoneDisplay(p.phone) : "—"}</td>
                                      <td>
                                        <PhotographyConsentBadge consent={p.photography_consent} />
                                      </td>
                                      <td className="text-center">
                                        <RowActionIcons
                                          aria-label={`Actions for ${[p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "participant"}`}
                                        >
                                          <button
                                            type="button"
                                            className="btn-link-muted"
                                            style={iconBtnStyle}
                                            title="Move back to Pending Check-In"
                                            aria-label="Move back to Pending Check-In"
                                            disabled={participantsLoading || undoBusy}
                                            onClick={() => requestUndoToPotential(p._id)}
                                          >
                                            <IconBacklog size={18} />
                                          </button>
                                        </RowActionIcons>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {eventQrTab === "noshow" && (
              <div className="event-qr-tab-panel" role="tabpanel">
                <div className="panel" style={{ minHeight: 0 }}>
                  <div className="panel-title">No-Show</div>
                  <p className="panel-hint" style={{ margin: "0 0 12px 0" }}>
                    Search participants marked no-show (they held a registration spot but did not attend) for the event
                    selected above. Changing the event reloads the list; it also updates after attendance changes.
                  </p>

                  {!selectedEventId ? (
                    <p className="panel-hint" style={{ margin: 0 }}>
                      Select an event above (or scan a check-in code on <strong>QR Check-In</strong>) to load this list.
                    </p>
                  ) : (
                    <>
                      <div className="toolbar">
                        <input
                          type="text"
                          className="input input-search-wide"
                          placeholder="Search by name, email, phone, or status (no-show)…"
                          value={noShowSearch}
                          onChange={(e) => setNoShowSearch(e.target.value)}
                          aria-label="Search no-show participants"
                        />
                        <button
                          type="button"
                          onClick={() => setNoShowSearch("")}
                          className="btn btn-secondary btn-sm"
                        >
                          Clear
                        </button>
                      </div>

                      <div className="event-qr-pending-scroll table-scroll">
                        {participantsLoading ? (
                          <p className="panel-hint" style={{ margin: "8px 0" }}>
                            Loading participants…
                          </p>
                        ) : noShowTableRows.length === 0 ? (
                          <p className="panel-hint" style={{ margin: "8px 0" }}>
                            No no-show participants for this event, or no matches for your search.
                          </p>
                        ) : (
                          <div className="table-wrapper">
                            <table>
                              <thead>
                                <tr>
                                  <th className="sortable" onClick={() => handleNoShowSort("participant_name")}>
                                    Participant
                                  </th>
                                  <th className="sortable" onClick={() => handleNoShowSort("registered_at")}>
                                    Registered
                                  </th>
                                  <th className="sortable" onClick={() => handleNoShowSort("reg_type")}>
                                    Reg Type
                                  </th>
                                  <th className="sortable" onClick={() => handleNoShowSort("status_label")}>
                                    Attendance
                                  </th>
                                  <th className="sortable" onClick={() => handleNoShowSort("email")}>
                                    Email
                                  </th>
                                  <th className="sortable" onClick={() => handleNoShowSort("phone")}>
                                    Phone
                                  </th>
                                  <th className="sortable" onClick={() => handleNoShowSort("photo_consent_sort")}>
                                    Photo consent
                                  </th>
                                  <th className="text-center" style={{ minWidth: 160 }}>
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {noShowTableRows.map((p) => {
                                  const att = (p.attendance || "").trim();
                                  const statusLabel = att === "No-Show" ? "No-Show" : att || "—";
                                  const emailRaw =
                                    p.email != null && String(p.email).trim() ? String(p.email).trim() : "";
                                  const email = emailRaw && !isPlaceholderEmail(emailRaw) ? emailRaw : "";
                                  const regTypeCell = (p.reg_type != null ? String(p.reg_type) : "").trim() || "—";
                                  return (
                                    <tr key={p._id}>
                                      <td>{[p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "—"}</td>
                                      <td style={{ whiteSpace: "nowrap" }}>{getParticipantRegisteredAtCell(p)}</td>
                                      <td style={{ whiteSpace: "nowrap" }}>{regTypeCell}</td>
                                      <td>{statusLabel}</td>
                                      <td className="table-cell-ellipsis" title={email || undefined}>
                                        {email || "—"}
                                      </td>
                                      <td>{normalizePhoneDigits(p.phone) ? formatPhoneDisplay(p.phone) : "—"}</td>
                                      <td>
                                        <PhotographyConsentBadge consent={p.photography_consent} />
                                      </td>
                                      <td className="text-center">
                                        <RowActionIcons
                                          aria-label={`Actions for ${[p.first_name, p.last_name].filter(Boolean).join(" ").trim() || "participant"}`}
                                        >
                                          <button
                                            type="button"
                                            className="btn-link"
                                            style={iconBtnStyle}
                                            title="Check in"
                                            aria-label="Check in"
                                            disabled={participantsLoading || undoBusy || potentialBulkBusy}
                                            onClick={() => beginManualCheckIn([p._id])}
                                          >
                                            <IconApprove size={18} />
                                          </button>
                                          <button
                                            type="button"
                                            className="btn-link-muted"
                                            style={iconBtnStyle}
                                            title="Move back to Pending Check-In"
                                            aria-label="Move back to Pending Check-In"
                                            disabled={participantsLoading || undoBusy}
                                            onClick={() => requestUndoToPotential(p._id)}
                                          >
                                            <IconBacklog size={18} />
                                          </button>
                                        </RowActionIcons>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            {canRecordClose && eventQrTab === "record_close" && (
              <div className="event-qr-tab-panel event-qr-tab-panel--record-close" role="tabpanel">
                <CloseEventRecord
                  embedded
                  preselectedEvent={selectedEvent}
                  onClosed={handleRecordCloseFinished}
                />
              </div>
            )}
          </div>
        </div>

        {eventPickModal && eventPickModal.choices && eventPickModal.choices.length > 0 ? (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1150,
              padding: "12px",
              boxSizing: "border-box",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setEventPickModal(null);
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="event-pick-scan-title"
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "10px",
                maxWidth: "480px",
                width: "100%",
                boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
                padding: "16px 18px 14px",
                boxSizing: "border-box",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3
                id="event-pick-scan-title"
                style={{ margin: "0 0 8px 0", fontSize: "1rem", fontWeight: 600, color: "#212529" }}
              >
                Which event are they checking in to?
              </h3>
              <p style={{ margin: "0 0 14px 0", fontSize: "13px", color: "#374151" }}>
                {eventPickModal.choices[0]?.participantName || "This guest"} is registered for more than one event
                today. Pick the correct session.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "12px" }}>
                {eventPickModal.choices.map((c) => (
                  <button
                    key={c.participant_id}
                    type="button"
                    className="btn btn-primary"
                    style={{ textAlign: "left", whiteSpace: "normal", lineHeight: 1.35 }}
                    onClick={() => handleEventPickChoice(c.participant_id)}
                  >
                    <strong>{c.eventName}</strong>
                    <br />
                    <span style={{ fontWeight: 400, fontSize: "12px", opacity: 0.92 }}>{c.eventDateTime}</span>
                    {(c.attendance || "").trim() === "Waitlisted" ? (
                      <span style={{ fontWeight: 600, fontSize: "12px", marginLeft: "6px" }}>(Waitlist)</span>
                    ) : null}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEventPickModal(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <ConfirmationModal
          open={Boolean(waitlistConfirm)}
          title="Waitlisted participant"
          message={
            waitlistConfirm ? (
              <WaitlistConfirmMessage
                participantName={waitlistConfirm.participantName}
                eventName={waitlistConfirm.eventName}
                eventDateTime={waitlistConfirm.eventDateTime}
                photography_consent={waitlistConfirm.photography_consent}
              />
            ) : null
          }
          mode="confirm"
          confirmLabel="Allow check-in"
          cancelLabel="Don't allow"
          onConfirm={async () => {
            const p = waitlistConfirm;
            if (!p) return;
            setWaitlistConfirm(null);
            setProcessing(true);
            try {
              await completeWaitlistAllowCheckIn(p, buildCheckInFlowCtx());
            } catch (err) {
              setResult({
                participantName: p.participantName,
                eventName: p.eventName,
                eventDateTime: p.eventDateTime,
                error: err.message || "Something went wrong.",
                registration_metrics: p.registration_metrics,
                photography_consent: p.photography_consent,
              });
            } finally {
              setProcessing(false);
            }
          }}
          onCancel={() => {
            const p = waitlistConfirm;
            setWaitlistConfirm(null);
            if (!p) return;
            setResult({
              participantName: p.participantName,
              eventName: p.eventName,
              eventDateTime: p.eventDateTime,
              status: "waitlist_declined",
              registration_metrics: p.registration_metrics,
              photography_consent: p.photography_consent,
            });
          }}
        />

        <ConfirmationModal
          open={Boolean(result && !processing && result.status !== "loading")}
          title={result && result.status !== "loading" ? scanResultModalTitle(result) : ""}
          message={
            result && result.status !== "loading"
              ? buildScanResultModalContent(
                  result,
                  result.status === "capacity_maxed" && result.event && result.participant_id ? (
                    (() => {
                      return (
                        <div style={{ marginTop: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
                          <div className="event-qr-capacity-stepper">
                            <button
                              type="button"
                              className="event-qr-capacity-stepper__btn"
                              disabled={capacityBumpBusy || capacityBumpDelta <= 1}
                              onClick={() => setCapacityBumpDelta((prev) => clampCapacityBumpDelta(prev - 1))}
                              aria-label="Decrease capacity increase amount"
                              title="Decrease (min 1)"
                            >
                              −
                            </button>
                            <input
                              className="event-qr-capacity-stepper__input"
                              type="number"
                              min={1}
                              step={1}
                              inputMode="numeric"
                              disabled={capacityBumpBusy}
                              value={capacityBumpDelta}
                              onChange={(e) => setCapacityBumpDelta(clampCapacityBumpDelta(e.target.value))}
                              aria-label="Capacity increase amount"
                            />
                            <button
                              type="button"
                              className="event-qr-capacity-stepper__btn"
                              disabled={capacityBumpBusy}
                              onClick={() => setCapacityBumpDelta((prev) => clampCapacityBumpDelta(prev + 1))}
                              aria-label="Increase capacity increase amount"
                              title="Increase"
                            >
                              +
                            </button>
                          </div>

                          {capacityBumpError ? (
                            <div
                              style={{
                                marginTop: "2px",
                                fontSize: "13px",
                                color: "#842029",
                                padding: "8px 10px",
                                background: "#f8d7da",
                                border: "1px solid #f5c2c7",
                                borderRadius: "6px",
                              }}
                            >
                              {capacityBumpError}
                            </div>
                          ) : null}
                        </div>
                      );
                    })()
                  ) : null
                )
              : null
          }
          mode={result && result.status === "capacity_maxed" ? "confirm" : "alert"}
          confirmLabel={result && result.status === "capacity_maxed" ? "Confirm & Check-in" : "OK"}
          cancelLabel={result && result.status === "capacity_maxed" ? "Cancel" : undefined}
          confirmDisabled={capacityBumpBusy}
          onConfirm={() => {
            if (result && result.status === "capacity_maxed") {
              void handleIncreaseEventCapacityFromModal(capacityBumpDelta);
              return;
            }
            setResult(null);
            const pending = manualCheckInResumeQueueRef.current;
            if (pending && pending.length > 0) {
              manualCheckInResumeQueueRef.current = null;
              void executeManualCheckInQueueRef.current(pending);
            }
          }}
          onCancel={() => {
            setResult(null);
            manualCheckInResumeQueueRef.current = null;
          }}
        />
      </div>
    </div>
  );
}

export default EventQRCheckIn;
